import {
  useEffect,
  useRef,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { ArrowIcon } from 'assets/icons'
import {
  Box,
  Drawer,
  DrawerOverlay,
  IconButton,
  Stack,
  Text
} from '@chakra-ui/react'
import {
  isAuth,
  isNotMobile,
  showCoordParams
} from 'utils/helpers'
import {
  MODAL_SECTION,
  SUCCEEDED
} from 'utils/constants'
import {
  authSelectors,
  handleOpenModal,
  markersSelectors,
  setCloseAllDrawers,
  setEventData,
  setHideChat,
  setHideNotification,
  setOpenPopupEvents,
  usersSelectors
} from 'store'
import { COLORS } from 'utils/styles'

import { AskToSignUpModal } from 'components/Content/AskToSignUpModal'
import { Content } from 'components/Content/Map/Markers/EventMarkerPopup/Content'
import { StyledDrawerContent } from './styled'

export const EventModal = () => {
  const dispatch = useDispatch()
  const { signInStatus } = useSelector(authSelectors)
  const {
    closeAllDrawers,
    getUserProfileStatus,
    getUserStatus,
    openPopupEvents
  } = useSelector(usersSelectors)
  const { eventData } = useSelector(markersSelectors)

  const drawerRef = useRef()
  const [animateOut, setAnimateOut] = useState(false)
  const [hoveringChevron, setHoveringChevron] = useState(false)
  const [askToSignUp, setAskToSignUp] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const isLoggedIn = signInStatus === SUCCEEDED || isAuth()
  const getDrawerHeight = (window.innerHeight - 120) + 'px'

  const closeDrawer = () => {
    setAskToSignUp(false)
    setAnimateOut(true)

    if (
      isLoggedIn
      && getUserProfileStatus === SUCCEEDED
      && !sessionStorage.getItem('eventid')
    ) {
      dispatch(setHideNotification(false))
      dispatch(setHideChat(false))
      dispatch(handleOpenModal({
        isOpen: true,
        section: MODAL_SECTION.RightSection
      }))
    }

    setTimeout(() => {
      setAnimateOut(false)
      dispatch(setOpenPopupEvents(false))

      setTimeout(() => {
        if (document.querySelectorAll('.chakra-modal__content').length === 0) {
          showCoordParams(setSearchParams)
        }
      }, 200)

      dispatch(setEventData(null))
    }, 1200)
  }

  const chevronDirection = (type) => {
    if (isNotMobile() && type === 'icon') {
      return 'rotate(-90deg)'
    }

    if (isNotMobile() && type === 'text') {
      return 'rotate(90deg)'
    }

    return 'rotate(180deg)'
  }

  const transition = {
    type: 'tween',
    stiffness: 200,
    damping: 22,
    duration: 0.8
  }

  const animateVariants = {
    WideScreen: {
      initial: {
        width: '368px',
        height: animateOut ? getDrawerHeight : '0px',
        y: 0,
        paddingBottom: '20px'
      },
      animate: {
        height: animateOut ? '0px' : getDrawerHeight,
        y: 0,
        paddingBottom: '0px',
        transition
      }
    },
    NarrowScreen: {
      initial: {
        width: '95%',
        height: getDrawerHeight,
        marginRight: '10px',
        x: 450
      },
      animate: {
        transition,
        x: 0,
        ...(animateOut ? { marginRight: '-450px' } : {})
      }
    }
  }

  useEffect(() => {
    if (
      isLoggedIn
      && getUserStatus === SUCCEEDED
      && sessionStorage.getItem('eventid')
      && askToSignUp
    ) {
      setAskToSignUp(false)
    } else if (
      isLoggedIn
      && getUserProfileStatus === SUCCEEDED
      && sessionStorage.getItem('eventid')
    ) {
      dispatch(setCloseAllDrawers(true))
      setTimeout(() => dispatch(setCloseAllDrawers(false)), 850)
    }
  }, [isLoggedIn, getUserProfileStatus, getUserStatus])

  useEffect(() => {
    if (closeAllDrawers) {
      closeDrawer()
    }
  }, [closeAllDrawers])

  return (
    <Drawer
      id="event-drawer"
      finalFocusRef={drawerRef}
      isOpen={openPopupEvents}
      onClose={() => closeDrawer()}
      placement="bottom"
      size="sm"
      closeOnEsc={false}
      closeOnOverlayClick
      boxShadow="2xl"
      blockScrollOnMount={false}
    >
      <DrawerOverlay
        id="event-modal-overlay"
        zIndex="1300"
        background="transparent"
        onClick={() => {
          if (isLoggedIn && !askToSignUp) {
            dispatch(setCloseAllDrawers(true))
            setTimeout(() => dispatch(setCloseAllDrawers(false)), 850)

            const customMarkerPopupOverlay = document.querySelector('div#custom-popup-overlay')
            if (customMarkerPopupOverlay) {
              customMarkerPopupOverlay.click()
            }
          }
        }}
      />

      {
        isLoggedIn
        && !askToSignUp
          ? ''
          : (
            <Stack
              position="absolute"
              backgroundColor="transparent"
              top="0"
              left="0"
              width="100%"
              height="100%"
              zIndex="1399"
              cursor="auto"
              onClick={() => {
                if (!isLoggedIn && !askToSignUp) {
                  setAskToSignUp(true)
                }
              }}
            />
          )
      }

      <StyledDrawerContent
        variants={animateVariants[isNotMobile() ? 'WideScreen' : 'NarrowScreen']}
        initial="initial"
        animate="animate"
        {...(isNotMobile() ? {} : { maxWidth: '450px' })}
        transformOrigin={isNotMobile() ? 'right' : 'bottom'}
        onClick={() => {
          if (!isLoggedIn && !askToSignUp) {
            setAskToSignUp(true)
          }
        }}
        cursor={isLoggedIn && !askToSignUp ? 'auto' : 'pointer'}
      >
        <AskToSignUpModal
          dynamicOpenModal={askToSignUp}
          eventData={eventData}
          type="events"
        />

        <Stack
          alignItems="center"
          justifyContent="center"
          direction="row"
          p="0px 5px"
          background={COLORS.goldGradientRightToLeft}
          minHeight="48px"
          borderRadius="10px 10px 0 0"
          marginTop="-1px"
          position="sticky"
          top="-0.2"
          marginBottom="-7px"
          zIndex={1000}
          onClick={() => {
            if (!isLoggedIn && !askToSignUp) {
              setAskToSignUp(true)
            }
          }}
        >
          <Text
            maxWidth="70%"
            fontSize="24px"
            textAlign="center"
          >
            Event
          </Text>

          <IconButton
            id="event-modal-back-button"
            outline="none"
            boxShadow="none"
            position="absolute"
            right="1.5"
            background="unset"
            transform={chevronDirection('icon')}
            transition="none"
            css={{
              ':focus': {
                outline: 'none',
                boxShadow: 'none'
              }
            }}
            icon={(
              <>
                <Text
                  fontWeight="300"
                  transform={chevronDirection('text')}
                  display={hoveringChevron ? 'flex' : 'none'}
                >
                  Exit
                </Text>

                <Stack display={hoveringChevron ? 'none' : 'flex'}>
                  <ArrowIcon height="15px" />
                </Stack>
              </>
            )}
            _hover={{ background: COLORS.gold_hover }}
            onClick={() => {
              if (isLoggedIn && !askToSignUp) {
                closeDrawer()
                setHoveringChevron(false)
              }
            }}
            onPointerEnter={(e) => {
              if (e.pointerType === 'mouse') {
                setHoveringChevron(true)
              }
            }}
            onPointerLeave={(e) => {
              if (e.pointerType === 'mouse') {
                setHoveringChevron(false)
              }
            }}
          />
        </Stack>

        <Stack
          id="force-close-event-drawer"
          display="none"
          onClick={() => closeDrawer()}
        />

        <Box pointerEvents={isLoggedIn && !askToSignUp ? 'auto' : 'none'}>
          <Content />
        </Box>
      </StyledDrawerContent>
    </Drawer>
  )
}
